import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingSection = () => {
  const plans = [
    {
      type: "job-seeker",
      title: "For Job Seekers",
      price: "Free",
      description: "Create your anonymous profile and get discovered by top employers",
      features: [
        "Anonymous profile creation",
        "Skills-based matching",
        "Unlimited profile views",
        "Direct employer contact",
        "Privacy protection",
        "Profile analytics",
        "Mobile app access",
        "24/7 support"
      ],
      cta: "Create Profile",
      ctaLink: "/job-seeker-registration",
      popular: false,
      color: "primary"
    },
    {
      type: "employer",
      title: "For Employers",
      price: "149 NOK",
      priceUnit: "per unlock",
      description: "Search candidates for free, pay only when you find the right match",
      features: [
        "Unlimited candidate search",
        "Advanced filtering options",
        "Anonymous profile browsing",
        "Pay-per-unlock model",
        "Direct messaging system",
        "Candidate shortlisting",
        "Team collaboration tools",
        "Hiring analytics"
      ],
      cta: "Start Searching",
      ctaLink: "/employer-registration",
      popular: true,
      color: "accent"
    }
  ];

  return (
    <section id="pricing" className="py-20 lg:py-32 bg-background">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-success/10 text-success text-sm font-medium mb-6">
            <Icon name="DollarSign" size={16} className="mr-2" />
            Transparent Pricing
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Simple, Fair Pricing
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            No hidden fees, no monthly subscriptions. Job seekers use Workfolio completely free, 
            while employers pay only when they find candidates worth contacting.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans?.map((plan, index) => (
            <div 
              key={index}
              className={`relative bg-card rounded-3xl p-8 lg:p-10 shadow-elevation border-2 transition-all duration-300 hover:shadow-elevation-lg hover:-translate-y-1 ${
                plan?.popular 
                  ? 'border-accent bg-gradient-to-br from-card to-accent/5' :'border-border hover:border-primary/30'
              }`}
            >
              {/* Popular Badge */}
              {plan?.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-accent text-accent-foreground px-4 py-2 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                </div>
              )}
              
              {/* Header */}
              <div className="text-center mb-8">
                <div className={`w-16 h-16 rounded-2xl bg-${plan?.color}/10 flex items-center justify-center mx-auto mb-4`}>
                  <Icon 
                    name={plan?.type === 'job-seeker' ? 'User' : 'Building2'} 
                    size={32} 
                    className={`text-${plan?.color}`} 
                  />
                </div>
                
                <h3 className="text-2xl font-bold text-foreground mb-2">
                  {plan?.title}
                </h3>
                
                <div className="mb-4">
                  <span className="text-4xl lg:text-5xl font-bold text-foreground">
                    {plan?.price}
                  </span>
                  {plan?.priceUnit && (
                    <span className="text-muted-foreground ml-2">
                      {plan?.priceUnit}
                    </span>
                  )}
                </div>
                
                <p className="text-muted-foreground">
                  {plan?.description}
                </p>
              </div>
              
              {/* Features */}
              <div className="space-y-4 mb-8">
                {plan?.features?.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center space-x-3">
                    <Icon name="Check" size={20} className={`text-${plan?.color} flex-shrink-0`} />
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* CTA */}
              <Button 
                variant={plan?.popular ? "default" : "outline"}
                size="lg"
                fullWidth
                asChild
                className="text-lg"
              >
                <Link to={plan?.ctaLink}>
                  <Icon 
                    name={plan?.type === 'job-seeker' ? 'UserPlus' : 'Search'} 
                    size={20} 
                    className="mr-2" 
                  />
                  {plan?.cta}
                </Link>
              </Button>
            </div>
          ))}
        </div>
        
        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="bg-muted/30 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-semibold text-foreground mb-4">
              Why Our Pricing Model Works
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <Icon name="Users" size={32} className="text-primary mx-auto mb-3" />
                <h4 className="font-semibold text-foreground mb-2">More Candidates</h4>
                <p className="text-sm text-muted-foreground">
                  Free access attracts more job seekers, giving employers a larger talent pool
                </p>
              </div>
              
              <div className="text-center">
                <Icon name="Target" size={32} className="text-accent mx-auto mb-3" />
                <h4 className="font-semibold text-foreground mb-2">Quality Focus</h4>
                <p className="text-sm text-muted-foreground">
                  Pay-per-unlock ensures employers only invest in candidates they're serious about
                </p>
              </div>
              
              <div className="text-center">
                <Icon name="TrendingUp" size={32} className="text-success mx-auto mb-3" />
                <h4 className="font-semibold text-foreground mb-2">Better ROI</h4>
                <p className="text-sm text-muted-foreground">
                  No wasted spend on subscriptions - pay only for actual hiring opportunities
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;